import Papa from 'papaparse';

const CACHE_VERSION = 'v2';
const CACHE_TTL_MS = 60 * 60 * 1000;

export interface CsvRow {
  ts: number;
  ean: string;
  type: string;
  volume: number;
}

export interface MonthSplit {
  weekday: number;
  weekend: number;
  total: number;
}

function getCacheKey(url: string): string {
  return `csv_cache_${CACHE_VERSION}_${btoa(url).slice(0, 40)}`;
}

function getCachedCsv(url: string): CsvRow[] | null {
  try {
    const key = getCacheKey(url);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { rows, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return rows;
  } catch {
    return null;
  }
}

function setCachedCsv(url: string, rows: CsvRow[]): void {
  try {
    const key = getCacheKey(url);
    localStorage.setItem(key, JSON.stringify({ rows, ts: Date.now() }));
  } catch {
    // localStorage plein — on ignore silencieusement
  }
}

function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  try {
    const parts = dateStr.trim().split(' ');
    if (!parts[0]) return null;
    const dateParts = parts[0].split('/');
    if (dateParts.length !== 3) return null;
    const [day, month, year] = dateParts;
    if (!day || !month || !year) return null;
    const time = parts[1] || '00:00';
    const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${time}`;
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

export function parseCsvUrl(url: string): Promise<CsvRow[]> {
  return new Promise((resolve, reject) => {
    const cached = getCachedCsv(url);
    if (cached) {
      resolve(cached);
      return;
    }

    Papa.parse(url, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const allRows: CsvRow[] = [];
        for (const r of result.data as any[]) {
          if (!r['Date Début'] || !r['EAN'] || !r['Type de volume']) continue;
          const ean = r['EAN'].trim();
          const date = parseDate(r['Date Début']);
          if (!date) continue;
          const volumeStr = r['Volume (kWh)'];
          const volume = parseFloat(volumeStr ? volumeStr.toString().replace(',', '.') : '0') || 0;
          const type = r['Type de volume'].trim();
          allRows.push({ ts: date.getTime(), ean, type, volume });
        }
        setCachedCsv(url, allRows);
        resolve(allRows);
      },
      error: (err) => reject(err),
    });
  });
}

export function monthKeyFromTs(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function isWeekend(ts: number): boolean {
  const day = new Date(ts).getDay();
  return day === 0 || day === 6;
}

export async function loadParticipantVolumeSplit(
  ean: string,
  year: number,
  csvUrls: string[],
  volumeType: string
): Promise<Record<string, MonthSplit>> {
  const split: Record<string, MonthSplit> = {};

  const results = await Promise.allSettled(csvUrls.map((url) => parseCsvUrl(url)));

  for (const result of results) {
    if (result.status !== 'fulfilled') continue;
    for (const row of result.value) {
      if (row.ean !== ean || row.type !== volumeType) continue;
      const monthKey = monthKeyFromTs(row.ts);
      if (!monthKey.startsWith(String(year))) continue;
      if (!split[monthKey]) split[monthKey] = { weekday: 0, weekend: 0, total: 0 };
      split[monthKey].total += row.volume;
      if (isWeekend(row.ts)) {
        split[monthKey].weekend += row.volume;
      } else {
        split[monthKey].weekday += row.volume;
      }
    }
  }

  return split;
}
